import { Button } from "@/components/flexkit/Button";
import { Nudge } from "@/components/nudges/Nudge";
import { Text } from "@/components/podkit/typography/Text";
import { NudgeKeys, useNudges } from "@/hooks/use-nudges";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useSetPreference, useListPreferences } from "@/queries/user-preferences-queries";
import { useCallback, useEffect, useState, type FC } from "react";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { toast } from "@/components/podkit/toasts/use-toast";
import { IconExternalLink } from "@/assets/icons/geist/IconExternalLink";

export const HOSTED_RUNNER_WAITLIST_KEY = "HOSTED_RUNNER_WAITLIST_JOINED";

export const HostedRunnerNudge: FC = () => {
    const { active: nudge, error, isPending } = useNudges();
    const { mutate: setPreference } = useSetPreference();
    const { preferences } = useListPreferences();
    const [open, setOpen] = useState(false);
    const [waitListJoined, setWaitListJoined] = useState(false);
    const segmentTrack = useSegmentTrack();

    // Check if user has already joined waitlist
    useEffect(() => {
        if (!preferences) return;
        const hasJoined = preferences.find((pref) => pref.key === HOSTED_RUNNER_WAITLIST_KEY)?.value === "true";
        setWaitListJoined(hasJoined);
    }, [preferences]);

    // Auto-show nudge if applicable
    useEffect(() => {
        if (isPending || error) {
            return;
        }

        if (nudge?.key === NudgeKeys.HOSTED_RUNNER) {
            setOpen(true);
            return;
        }
        setOpen(false);
    }, [isPending, error, nudge?.key]);

    const toggleOpen = useCallback(() => {
        setOpen((previousOpenState) => {
            const isNowOpen = !previousOpenState;
            if (!isNowOpen) {
                setPreference({
                    key: NudgeKeys.HOSTED_RUNNER,
                    value: new Date().toISOString(),
                });
            }
            return isNowOpen;
        });
    }, [setPreference]);

    const onJoinWaitlist = useCallback(() => {
        segmentTrack("waitlist_joined", { type: "infrastructure", provider: "Gitpod" });
        setWaitListJoined(true);
        // Store waitlist joined state in preferences
        setPreference({
            key: HOSTED_RUNNER_WAITLIST_KEY,
            value: "true",
        });
        toast({
            title: "You're on the waitlist!",
            description: "We will let you know once Gitpod-hosted compute is available",
        });
    }, [segmentTrack, setPreference]);

    const ariaLabelPrefix = "Announcement - Gitpod-hosted compute";
    return (
        <Nudge
            open={open}
            toggleOpen={toggleOpen}
            ariaLabelPrefix={ariaLabelPrefix}
            data-testid="nudge-hosted-runner-trigger"
            data-track-location="hosted_runner_nudge"
        >
            <div className="flex flex-col gap-2">
                <div className="p-2 text-center">
                    <Text className="text-base font-bold text-content-primary">Gitpod-hosted compute</Text>
                    <Text className="text-base text-content-secondary">
                        Use our infrastructure to run environments. {waitListJoined && <span>Coming soon.</span>}
                    </Text>
                </div>
                <div className="flex flex-col gap-2">
                    {waitListJoined ? (
                        <Button
                            variant="primary"
                            size="md"
                            className="grow"
                            aria-label={`${ariaLabelPrefix} - Learn more`}
                            LeadingIcon={IconExternalLink}
                            asChild
                        >
                            <ExternalLink href="https://www.gitpod.io/blog/coming-soon-gitpod-hosted-compute">
                                Learn more
                            </ExternalLink>
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            size="md"
                            className="grow"
                            aria-label={`${ariaLabelPrefix} - Join waitlist`}
                            onClick={onJoinWaitlist}
                        >
                            Join waitlist
                        </Button>
                    )}
                </div>
            </div>
        </Nudge>
    );
};
